// Copyright 2025, University of Maryland, All Rights Reserved
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { useServices } from '@/app-shell/services'
import type {
  KnowledgeBaseContextConfig,
  KnowledgeBaseData,
  KnowledgeBaseStore,
} from '../types'
import {
  createItem,
  deleteItem,
  fetchKnowledgeBase,
  updateItem,
} from './knowledgeBase.api'
import { knowledgeBaseQueryKey } from './queryKeys'
import type { RawKnowledgeBaseSettings } from '../utils/settings'
import { readSettingsFromConfig } from '../utils/settings'

const KnowledgeBaseContext = createContext<KnowledgeBaseStore | null>(null)

export function KnowledgeBaseProvider({ children, ...props }: PropsWithChildren<KnowledgeBaseContextConfig>): JSX.Element {
  const services = useServices()
  const client = services.getClient()
  const {
    workspaceId,
    wsId: wsIdProp,
    orgId = 'umd-org',
    nodeInstanceId,
    nodeDefinition: _nodeDefinition,
    config,
  } = props

  const wsId = wsIdProp ?? workspaceId ?? 'default-workspace'

  const resolvedInstanceId = nodeInstanceId

  const basePath = useMemo(() => {
    if (!orgId || !wsId || !resolvedInstanceId) return null
    return `/org/${orgId}/ws/${wsId}/knowledge_base/instances/${resolvedInstanceId}`
  }, [orgId, wsId, resolvedInstanceId])

  const queryClient = useQueryClient()
  const queryKey = knowledgeBaseQueryKey(orgId, wsId, resolvedInstanceId)

  const { data: response, isLoading: queryLoading, error: queryError, refetch } = useQuery({
    queryKey,
    queryFn: () => fetchKnowledgeBase(client, basePath!),
    enabled: !!basePath,
    staleTime: 5 * 60_000,
  })

  const data = useMemo<KnowledgeBaseData>(() => ({
    items: response?.items ?? [],
    timestamp: {
      lastUpdated: response?.updatedAt ?? null,
      updatedBy: response?.updatedBy ?? null,
    },
  }), [response])

  const [saving, setSaving] = useState(false)
  const [mutationError, setMutationError] = useState<string | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const text = searchParams.get('q') ?? ''
  const filters = useMemo(() => ({ text }), [text])
  const setFilters = useCallback((updater: (prev: { text: string }) => { text: string }) => {
    const next = updater({ text })
    setSearchParams(prev => {
      const params = new URLSearchParams(prev)
      if (next.text) {
        params.set('q', next.text)
      } else {
        params.delete('q')
      }
      return params
    }, { replace: true })
  }, [text, setSearchParams])
  const [settings, setSettings] = useState<RawKnowledgeBaseSettings>(() => readSettingsFromConfig(config))

  // Sync settings from server response into local state when query data arrives.
  // Label and description arrive at the response top level but read like
  // settings everywhere in the UI, so fold them into the settings record.
  useEffect(() => {
    if (response?.settings) {
      setSettings({
        ...(response.settings as RawKnowledgeBaseSettings),
        label: response.label ?? '',
        description: response.description ?? '',
      })
    }
  }, [response])

  const loading = queryLoading
  const loadError = !basePath
    ? 'Knowledge base node requires a valid node instance id.'
    : queryError
      ? (queryError instanceof Error ? queryError.message : 'Failed to load knowledge base')
      : null
  const error = mutationError ?? loadError

  const setError = setMutationError

  const loadKnowledgeBase = useCallback(async () => {
    await refetch()
  }, [refetch])

  const withSaving = useCallback(
    async (operation: () => Promise<void>) => {
      setSaving(true)
      setMutationError(null)
      try {
        await operation()
        await queryClient.invalidateQueries({ queryKey })
      } finally {
        setSaving(false)
      }
    },
    [queryClient, queryKey],
  )

  const addItemAction = useCallback(
    (payload: {
      content: string
      score: number | null
      sectionType?: string
      sectionLabel?: string
      metadata?: Record<string, unknown>
      fields?: Record<string, string> | null
    }) =>
      withSaving(async () => {
        if (!basePath) return
        const metadata: Record<string, unknown> = { ...payload.metadata }
        if (payload.sectionType) metadata.sectionType = payload.sectionType
        if (payload.sectionLabel) metadata.sectionLabel = payload.sectionLabel

        await createItem(client, basePath, {
          content: payload.content,
          score: payload.score,
          metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
          fields: payload.fields ?? undefined,
        })
      }),
    [basePath, client, withSaving],
  )

  const updateItemAction = useCallback(
    (payload: {
      itemId: string
      content: string
      score: number | null
      sectionType?: string
      sectionLabel?: string
      metadata?: Record<string, unknown>
      fields?: Record<string, string> | null
    }) =>
      withSaving(async () => {
        if (!basePath) return
        const metadata: Record<string, unknown> = { ...payload.metadata }
        if (payload.sectionType) metadata.sectionType = payload.sectionType
        if (payload.sectionLabel) metadata.sectionLabel = payload.sectionLabel

        await updateItem(client, basePath, payload.itemId, {
          content: payload.content,
          score: payload.score,
          metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
          fields: payload.fields ?? undefined,
        })
      }),
    [basePath, client, withSaving],
  )

  const deleteItemAction = useCallback(
    (itemId: string) =>
      withSaving(async () => {
        if (!basePath) return
        await deleteItem(client, basePath, itemId)
      }),
    [basePath, client, withSaving],
  )

  const value = useMemo<KnowledgeBaseStore>(() => ({
    data,
    settings,
    loading,
    saving,
    error,
    filters,
    reload: loadKnowledgeBase,
    addItem: addItemAction,
    updateItem: updateItemAction,
    deleteItem: deleteItemAction,
    setError,
    setFilters,
  }), [
    data,
    settings,
    loading,
    saving,
    error,
    filters,
    loadKnowledgeBase,
    addItemAction,
    updateItemAction,
    deleteItemAction,
    setFilters,
  ])

  return (
    <KnowledgeBaseContext.Provider value={value}>
      {children}
    </KnowledgeBaseContext.Provider>
  )
}

export function useKnowledgeBaseStore(): KnowledgeBaseStore {
  const context = useContext(KnowledgeBaseContext)
  if (!context) {
    throw new Error('useKnowledgeBaseStore must be used within KnowledgeBaseProvider')
  }
  return context
}
